import React, {Component} from "react"

import {
  Play,
  Pause,
  StepBack,
  StepForward,
  FastForward,
  Info,
  Crop,
  Volume2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

import {
  EVENTS,
  PROPERTIES
} from './constants'

import {
  timecodeToString,
  timecodeToFrame,
  frameToTimecode
} from '../../library/general'

import './index.css'

// Borderless icon button meant to live inside a ButtonGroup (segmented control)
const IconButton = ({ tip, active, onClick, children, className }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        onClick={onClick}
        className={cn(
          'h-9 w-10 rounded-none text-foreground/80 hover:bg-accent hover:text-foreground',
          active && 'bg-accent text-primary hover:bg-accent hover:text-primary',
          className
        )}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{tip}</TooltipContent>
  </Tooltip>
)

const ButtonGroup = ({ children, className }) => (
  <div className={cn('inline-flex h-9 items-stretch overflow-hidden rounded-md border bg-card shadow-sm divide-x', className)}>
    {children}
  </div>
)

export default class ProxyPlayer extends Component {
  constructor(props) {
    super(props)

    this.updateState = this.updateState.bind(this)

    this.state = {
      bindEventsComplete: false,
      loading: true,
      seeking: false,
      volume: 0.5,
      centercut: {
        show: false,
        height: 0,
        width: 0
      },
      inPoint: '',
      outPoint: '',
      programDuration: '',
      showShortcuts: false
    }
  }

  componentDidMount() {
    this.bindIfReady()
  }

  componentDidUpdate() {
    this.bindIfReady()
  }

  componentWillUnmount() {
    if (this.refPlayer) {
      EVENTS.forEach(event => {
        this.refPlayer.removeEventListener(event.toLowerCase(), this.updateState)
      })
    }
  }

  bindIfReady = () => {
    const {refPlayer, state} = this

    if (refPlayer && !state.bindEventsComplete) {
      EVENTS.forEach(event => {
        refPlayer.addEventListener(event.toLowerCase(), this.updateState)
      })

      refPlayer.volume = state.volume
      this.setState({bindEventsComplete: true})
    }
  }

  updateState = () => {
    const {
      refPlayer
    } = this

    this.setState(
      PROPERTIES.reduce((p, c) => {
        p[c] = refPlayer && refPlayer[c]
        return p
      }, {})
    )
  }

  playOrPause = () => {
    const {paused} = this.refPlayer
    paused ? this.refPlayer.play() : this.refPlayer.pause()
  }

  nextFrame = () => {
    const {frameRate} = this.props
    this.refPlayer.currentTime += (1 / frameRate)
  }

  prevFrame = () => {
    const {frameRate} = this.props
    this.refPlayer.currentTime -= (1 / frameRate)
  }

  panSeconds = s => {
    this.refPlayer.currentTime += s
  }

  gotoTime = s => {
    this.refPlayer.currentTime = s
  }

  playbackRate4x = () => {
    const {playbackRate, paused} = this.refPlayer

    if (playbackRate === 4) {
      this.refPlayer.playbackRate = 1
    }
    else {
      this.refPlayer.playbackRate = 4

      if (paused) {
        this.refPlayer.play()
      }
    }
  }

  changeVolume = v => {
    let volume = parseFloat(this.refPlayer.volume.toFixed(2)) + v

    if (!(volume < 0 || volume > 1))
      this.refPlayer.volume = volume
  }

  muteVolume = () => {
    const {volume} = this.refPlayer
    if (volume !== 0)
      this.refPlayer.volume = 0
    else
      this.refPlayer.volume = 0.5
  }

  getCurrentTime = () => {
    const {currentTime} = this.refPlayer
    const {frameRate} = this.props

    return timecodeToString(frameToTimecode(currentTime * frameRate, frameRate))
  }

  markTime = (where = 'inPoint') => {
    const {
      getCurrentTime,
      calculateProgramDuration
    } = this
    this.setState({
      [where]: getCurrentTime()
    }, () => calculateProgramDuration())
  }

  calculateProgramDuration = () => {
    const {inPoint, outPoint} = this.state
    const {frameRate} = this.props

    const inFrames = inPoint && timecodeToFrame(inPoint, frameRate)
    const outFrames = outPoint && timecodeToFrame(outPoint, frameRate)

    if (isNaN(inFrames) || isNaN(outFrames)) {
      return
    }

    if (inFrames > outFrames) {
      this.setState({
        programDuration: ''
      })
    }
    else {
      const programFrames = outFrames - inFrames
      const programDurationTimecode = timecodeToString(frameToTimecode(programFrames, frameRate))

      this.setState({
        programDuration: programDurationTimecode
      })
    }
  }

  handleHotKeyDown = e => {
    const {
      key,
      altKey,
      shiftKey
    } = e

    const {
      playbackRate,
      duration
    } = this.state

    const {
      playOrPause,
      playbackRate4x,
      nextFrame,
      prevFrame,
      panSeconds,
      changeVolume,
      muteVolume,
      gotoTime,
      markTime
    } = this

    switch (key) {
      case ' ':
        e.preventDefault()
        playOrPause()
        break

      case 'f':
        playbackRate === 1 && playbackRate4x()
        break

      case 'j':
        panSeconds(300)
        break;

      case 'a':
        panSeconds(-300)
        break

      case 'ArrowRight':
        e.preventDefault()
        !altKey && !shiftKey && panSeconds(10)
        !!altKey && nextFrame()
        !!shiftKey && panSeconds(300)
        break

      case 'ArrowLeft':
        e.preventDefault()
        !altKey && !shiftKey && panSeconds(-10)
        !!altKey && prevFrame()
        !!shiftKey && panSeconds(-300)
        break

      case 'ArrowUp':
        e.preventDefault()
        changeVolume(0.1)
        break

      case 'ArrowDown':
        e.preventDefault()
        changeVolume(-0.1)
        break

      case 'Home':
      case 'c':
        gotoTime(0)
        break

      case 'End':
      case 'v':
        gotoTime(duration - 10)
        break

      case 'h':
        panSeconds(10)
        break

      case 'g':
        nextFrame()
        break

      case 's':
        panSeconds(-10)
        break

      case 'd':
        prevFrame()
        break

      case '.':
        muteVolume()
        break

      case 'i':
        markTime('inPoint')
        break

      case 'o':
        markTime('outPoint')
        break

      default:
        break
    }
  }

  handleHotKeyUp = e => {
    if (e.key === 'f')
      this.playbackRate4x()
  }

  handleSeek = e => {
    this.refPlayer.currentTime = e.target.value
  }

  handleSeekMouseDown = e => {
    this.setState({
      seeking: true
    })
  }

  handleSeekMouseUp = e => {
    this.setState({
      seeking: false
    })
  }

  handleVolumeChange = e => {
    this.refPlayer.volume = e.target.value
    this.setState({
      volume: e.target.value
    })
  }

  showCenterCut = () => {
    const {aspectRatio} = this.props
    const {
      offsetHeight,
      offsetWidth
    } = this.refPlayer

    const show = !this.state.centercut.show
    const videoWidthReduced = offsetWidth - offsetWidth * 0.25

    this.setState({
      centercut: {
        show,
        height: offsetHeight,
        width: aspectRatio === '16:9' ? videoWidthReduced : offsetWidth,
      }
    })
  }

  render() {
    const {
      duration,
      currentTime,
      paused,
      loading,
      playbackRate,
      volume,
      centercut,
      inPoint,
      outPoint,
      programDuration,
      showShortcuts
    } = this.state

    const {
      frameRate,
      proxyLink
    } = this.props

    const {
      handleHotKeyDown,
      handleHotKeyUp,
      handleSeek,
      handleSeekMouseDown,
      handleSeekMouseUp,
      handleVolumeChange,
      playOrPause,
      prevFrame,
      nextFrame,
      playbackRate4x,
      showCenterCut,
      markTime
    } = this

    const timestamp = timecodeToString(frameToTimecode((currentTime || 0) * frameRate, frameRate))

    const shortcuts = [
      ['Play/Pause', 'Spacebar'],
      ['Next Frame', 'g or Alt (⌥) + →'],
      ['Previous Frame', 'd or Alt (⌥) + ←'],
      ['Next 10 Seconds', 'h or →'],
      ['Prev 10 Seconds', 's or ←'],
      ['Next 5 minutes', 'j or Shift + →'],
      ['Prev 5 minutes', 'a or Shift + ←'],
      ['Fast Forward 4x', 'Hold down f'],
      ['Volume Up / Down', '↑ / ↓'],
      ['Mute', '.'],
      ['Mark In-Point', 'i'],
      ['Mark Out-Point', 'o'],
      ['Go to start', 'c or Home'],
      ['Go to end', 'v or End'],
    ]

    return (
      <div className="ProxyPlayer p-3">
        <div
          className="wrapper"
          onKeyDown={handleHotKeyDown}
          onKeyUp={handleHotKeyUp}
          tabIndex="0"
          autoFocus
        >
          <div className="video-wrapper overflow-hidden rounded-md">
            <video
              id="proxy-player"
              ref={e => {
                this.refPlayer = e
              }}
              onCanPlay={() => this.setState({loading: false})}
              onClick={playOrPause}
            >
              <source
                src={proxyLink}
                id="proxy-player-source"
                type="video/mp4"
              />
              Your browser does not support HTML5 video.
            </video>
            {
              centercut.show &&
              <div
                className="centercut-overlay"
                style={{
                  width: centercut.width,
                  height: centercut.height
                }}
              >
              </div>
            }
            {
              loading &&
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Spinner size={28} className="text-white" />
              </div>
            }
          </div>

          {/* Scrub */}
          <div className="mt-3 flex h-9 items-center gap-3">
            <span className="w-28 shrink-0 rounded-md border bg-muted px-2 py-1.5 text-center font-mono text-sm tabular-nums">{timestamp}</span>
            <input
              type="range"
              id="scrub-slider"
              className="w-full"
              value={currentTime || 0}
              min="0"
              max={duration || 0}
              step="1"
              onInput={handleSeek}
              onChange={handleSeek}
              onMouseDown={handleSeekMouseDown}
              onMouseUp={handleSeekMouseUp}
            />
          </div>

          {/* Controls */}
          <div className="mt-3 grid grid-cols-1 items-center gap-3 sm:grid-cols-3">
            <div className="flex h-9 items-center gap-2">
              <Volume2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="range"
                id="volume-slider"
                className="w-full max-w-[160px]"
                value={volume}
                min="0"
                max="1"
                step="0.1"
                onInput={handleVolumeChange}
                onChange={handleVolumeChange}
              />
            </div>

            <div className="flex items-center justify-center" role="group">
              <ButtonGroup>
                <IconButton tip="Previous Frame (d or Alt (⌥) + ←)" onClick={prevFrame}>
                  <StepBack className="h-4 w-4" />
                </IconButton>
                <IconButton tip="Play/Pause (spacebar)" onClick={playOrPause} className="w-12">
                  {paused === false ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
                </IconButton>
                <IconButton tip="Next Frame (g or Alt (⌥) + →)" onClick={nextFrame}>
                  <StepForward className="h-4 w-4" />
                </IconButton>
                <IconButton tip="Fast Forward 4x (hold down f)" onClick={playbackRate4x} active={playbackRate === 4}>
                  <FastForward className="h-4 w-4" />
                </IconButton>
              </ButtonGroup>
            </div>

            <div className="flex items-center justify-end" role="group">
              <ButtonGroup>
                <IconButton tip="Player shortcuts" onClick={() => this.setState({showShortcuts: true})}>
                  <Info className="h-4 w-4" />
                </IconButton>
                <IconButton tip="Centercut overlay" onClick={showCenterCut} active={centercut.show}>
                  <Crop className="h-4 w-4" />
                </IconButton>
              </ButtonGroup>
            </div>
          </div>

          {/* In / Out points */}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="flex h-9 items-stretch">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-10 shrink-0 rounded-r-none bg-muted font-bold text-foreground/80 hover:bg-accent" onClick={() => markTime('inPoint')}>I</Button>
                </TooltipTrigger>
                <TooltipContent>Mark the In-Point of the Program (i)</TooltipContent>
              </Tooltip>
              <Input id="in-point-input" readOnly value={inPoint} placeholder="In-Point" className="h-9 min-w-0 rounded-l-none border-l-0 text-center font-mono tabular-nums" />
            </div>
            <div className="flex h-9 items-stretch">
              <Input id="out-point-input" readOnly value={outPoint} placeholder="Out-Point" className="h-9 min-w-0 rounded-r-none border-r-0 text-center font-mono tabular-nums" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-10 shrink-0 rounded-l-none bg-muted font-bold text-foreground/80 hover:bg-accent" onClick={() => markTime('outPoint')}>O</Button>
                </TooltipTrigger>
                <TooltipContent>Mark the Out-Point of the Program (o)</TooltipContent>
              </Tooltip>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex h-9 items-stretch">
                  <span className="inline-flex shrink-0 items-center rounded-l-md border border-r-0 bg-muted px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Duration
                  </span>
                  <Input id="program-duration" readOnly value={programDuration} placeholder="--:--:--:--" className={cn('h-9 min-w-0 rounded-l-none text-center font-mono tabular-nums', programDuration && 'font-semibold text-foreground')} />
                </div>
              </TooltipTrigger>
              <TooltipContent>When the In-Point and Out-Point of a program are marked, the program duration timecode is shown here</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <Dialog open={showShortcuts} onOpenChange={o => this.setState({showShortcuts: o})}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Player Shortcuts</DialogTitle>
            </DialogHeader>
            <p className="text-sm italic text-muted-foreground">Click on the video first to enable the shortcuts.</p>
            <table className="w-full text-sm">
              <tbody>
                {shortcuts.map(([action, keys]) => (
                  <tr key={action} className="border-b last:border-b-0">
                    <td className="py-1 pr-3">{action}</td>
                    <td className="py-1 font-mono text-xs text-muted-foreground">{keys}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <DialogFooter>
              <Button variant="outline" onClick={() => this.setState({showShortcuts: false})}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }
}
